import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Quote, ArrowRight } from "lucide-react";
import { Reveal, Eyebrow } from "@/components/Reveal";
import { TEAM } from "@/data/content";

const Initials = ({ name }) => (
  <div className="flex aspect-[4/5] w-full items-center justify-center bg-gradient-to-br from-teal to-teal-deep">
    <span className="font-display text-6xl font-extrabold text-sand/85">
      {name.split(" ").map((n) => n[0]).slice(0, 2).join("")}
    </span>
  </div>
);

const MemberCard = ({ member, index, onOpen }) => (
  <motion.button
    initial={{ opacity: 0, y: 40 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-40px" }}
    transition={{ duration: 0.6, delay: (index % 3) * 0.1, ease: [0.22, 1, 0.36, 1] }}
    onClick={() => onOpen(member)}
    data-testid={`team-card-${member.id}`}
    className="group w-full overflow-hidden rounded-[2rem] bg-white text-left shadow-[0_10px_40px_-24px_rgba(26,29,36,0.4)] transition-shadow duration-500 hover:shadow-[0_28px_60px_-24px_rgba(13,124,133,0.45)]"
  >
    <div className="relative overflow-hidden">
      {member.photo ? (
        <img
          src={member.photo}
          alt={member.name}
          loading="lazy"
          className="aspect-[4/5] w-full object-cover transition-transform duration-700 group-hover:scale-105"
        />
      ) : (
        <Initials name={member.name} />
      )}
      <span className="absolute bottom-4 left-4 rounded-full bg-coal-dark/80 px-3 py-1 font-mono-brand text-[10px] font-bold uppercase tracking-[0.2em] text-ember-light backdrop-blur">
        {member.tagline}
      </span>
    </div>
    <div className="p-6">
      <h3 className="font-display text-xl font-extrabold tracking-tight text-coal">
        <span className="bg-gradient-to-r from-ember/25 to-ember/0 bg-[length:100%_40%] bg-[position:0_88%] bg-no-repeat">
          {member.name}
        </span>
      </h3>
      <p className="mt-1.5 text-sm font-semibold text-teal">{member.role}</p>
      <span className="mt-4 inline-flex items-center gap-1.5 text-xs font-heading font-bold uppercase tracking-wider text-coal-muted transition-colors group-hover:text-ember">
        Read {member.name.split(" ")[0]}'s story
        <ArrowRight size={13} className="transition-transform duration-300 group-hover:translate-x-1" />
      </span>
    </div>
  </motion.button>
);

const MemberModal = ({ member, onClose }) => (
  <motion.div
    initial={{ opacity: 0 }}
    animate={{ opacity: 1 }}
    exit={{ opacity: 0 }}
    className="fixed inset-0 z-[90] flex items-end justify-center bg-coal-dark/80 backdrop-blur-sm sm:items-center sm:p-6"
    onClick={onClose}
    data-testid="team-modal"
  >
    <motion.div
      initial={{ y: 80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      exit={{ y: 60, opacity: 0 }}
      transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
      className="relative max-h-[92vh] w-full max-w-4xl overflow-y-auto rounded-t-[2rem] bg-sand sm:rounded-[2rem]"
      onClick={(e) => e.stopPropagation()}
    >
      <button
        data-testid="team-modal-close"
        onClick={onClose}
        aria-label="Close team member story"
        className="sticky left-full top-4 z-10 mr-4 flex h-10 w-10 -translate-x-0 items-center justify-center rounded-full bg-coal text-sand transition-colors hover:bg-ember"
      >
        <X size={18} />
      </button>
      <div className="grid gap-8 px-6 pb-10 sm:px-10 md:grid-cols-[0.8fr_1.2fr]">
        <div>
          <div className="overflow-hidden rounded-3xl md:sticky md:top-0">
            {member.photo ? (
              <img src={member.photo} alt={member.name} className="aspect-[4/5] w-full object-cover" />
            ) : (
              <Initials name={member.name} />
            )}
          </div>
        </div>
        <div>
          <p className="font-mono-brand text-xs font-bold uppercase tracking-[0.25em] text-ember">
            {member.tagline}
          </p>
          <h2 className="mt-2 font-display text-3xl font-extrabold tracking-tight text-coal sm:text-4xl">
            <span className="bg-gradient-to-r from-ember/30 to-ember/0 bg-[length:100%_35%] bg-[position:0_90%] bg-no-repeat">
              {member.name}
            </span>
          </h2>
          <p className="mt-2 font-heading text-base font-bold text-teal">{member.role}</p>
          <div className="mt-6 space-y-4">
            {member.bio.map((p, i) => (
              <p key={i} className="text-sm leading-relaxed text-coal-muted sm:text-base">
                {p}
              </p>
            ))}
          </div>
          <div className="mt-8 flex gap-4 rounded-2xl bg-teal-subtle p-5">
            <Quote size={22} className="shrink-0 text-teal" />
            <p className="font-heading text-sm font-bold italic leading-relaxed text-teal-deep sm:text-base">
              {member.quote}
            </p>
          </div>
        </div>
      </div>
    </motion.div>
  </motion.div>
);

export default function Team() {
  const [active, setActive] = useState(null);

  return (
    <div data-testid="team-page">
      <section className="relative overflow-hidden bg-teal-subtle grain py-16">
        <div className="absolute -right-24 -top-24 h-72 w-72 rounded-full bg-ember/15 blur-[90px]" />
        <div className="relative mx-auto max-w-4xl px-6 text-center">
          <Reveal>
            <Eyebrow>Our team</Eyebrow>
            <h1 className="mt-5 font-display text-4xl font-extrabold tracking-tight text-coal sm:text-5xl lg:text-6xl">
              Meet the <span className="text-ember">Team</span>
            </h1>
            <p className="mx-auto mt-6 max-w-2xl text-base leading-relaxed text-coal-muted md:text-lg">
              Educators, scientists, coaches and storytellers, united by one belief: no learner
              should have to shrink to fit a world that wasn't designed to see their potential.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="py-16">
        <div className="mx-auto max-w-7xl px-6">
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {TEAM.map((m, i) => (
              <MemberCard key={m.id} member={m} index={i} onOpen={setActive} />
            ))}
          </div>
        </div>
      </section>

      <AnimatePresence>
        {active && <MemberModal member={active} onClose={() => setActive(null)} />}
      </AnimatePresence>
    </div>
  );
}
